import {
    useRef,
    useState,
    useEffect,
    useCallback,
    useContext,
    useId,
    type PointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { OverlayOwnerContext } from "@/components/overlay";

export type DatePickerMode = "single" | "range";
export interface DateRange {
    start: string | null;
    end: string | null;
}
interface DatePickerProps {
    mode?: DatePickerMode;
    type?: "date" | "datetime" | "time";
    value?: string | null;
    onChange?: (value: string | null) => void;
    range?: DateRange;
    onRangeChange?: (range: DateRange) => void;
    initialMonth?: Date;
    minDate?: string;
    maxDate?: string;
    className?: string;
    display?: "inline" | "dropdown" | "modal";
    label?: string;
    placeholder?: string;
    clearable?: boolean;
    required?: boolean;
    disabled?: boolean;
    full?: boolean;
    format?: Intl.DateTimeFormatOptions;
    dropdownClassName?: string;
    error?: string;
}
const ymd = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const ordered = (a: string, b: string): DateRange =>
    a <= b ? { start: a, end: b } : { start: b, end: a };

export const DatePicker = ({
    mode = "single",
    type = "date",
    value,
    onChange,
    range,
    onRangeChange,
    initialMonth,
    minDate,
    maxDate,
    className = "",
    display = "modal",
    label,
    placeholder,
    clearable = false,
    required = false,
    disabled = false,
    full = false,
    format,
    dropdownClassName = "",
    error,
}: DatePickerProps) => {
    const { t, i18n } = useTranslation("global");
    const owner = useContext(OverlayOwnerContext);
    const id = useId();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const today = ymd(new Date());
    const selected =
        type === "time"
            ? null
            : (mode === "range" ? range?.start || range?.end : value)?.slice(
                  0,
                  10
              );
    const [month, setMonth] = useState(
        () => initialMonth || new Date(`${selected || today}T12:00:00`)
    );
    const [view, setView] = useState<"days" | "months" | "years">("days");
    const [endpoint, setEndpoint] = useState<"start" | "end">("start");
    const [hover, setHover] = useState<string | null>(null);
    const drag = useRef<{
        date: string;
        moved: boolean;
        previous: DateRange;
    } | null>(null);
    const suppressClick = useRef(false);
    const anchor = useRef<string | null>(null);
    const time =
        type === "time"
            ? value || "00:00"
            : value?.split("T")[1]?.slice(0, 5) || "00:00";

    // Popup logic
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [dropdownCoords, setDropdownCoords] = useState<{
        top?: number;
        bottom?: number;
        left?: number;
        width: number;
        placement: "top" | "bottom";
    }>({ width: 0, placement: "bottom" });

    const updateDropdownPosition = useCallback((): void => {
        if (!containerRef.current || display !== "dropdown") return;
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 350; // Approx calendar height

        const shouldPlaceTop =
            spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        const targetPlacement = shouldPlaceTop ? "top" : "bottom";

        setDropdownCoords({
            top: shouldPlaceTop ? undefined : Math.round(rect.bottom + 4),
            bottom: shouldPlaceTop
                ? Math.round(window.innerHeight - rect.top + 4)
                : undefined,
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            placement: targetPlacement,
        });
    }, [display]);

    const closeDropdown = useCallback((): void => {
        if (!isOpen || isClosing) return;
        if (display === "modal") {
            setIsOpen(false);
            return;
        }
        setIsClosing(true);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, 160);
    }, [isOpen, isClosing, display]);

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!isOpen && !isClosing) return;
        if (display === "modal") return;

        updateDropdownPosition();
        const handleWindowChange = () => updateDropdownPosition();
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                closeDropdown();
            }
        };
        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, isClosing, updateDropdownPosition, closeDropdown, display]);

    const disabledFn = (date: string): boolean =>
        Boolean((minDate && date < minDate) || (maxDate && date > maxDate));
    const choose = (date: string, shift = false): void => {
        if (disabledFn(date)) {
            return;
        }
        if (mode === "single") {
            onChange?.(type === "datetime" ? `${date}T${time}` : date);
            if (display !== "inline") closeDropdown();
            return;
        }
        const start = anchor.current || range?.start || range?.end;
        if (shift && start) {
            onRangeChange?.(ordered(start, date));
            return;
        }
        anchor.current = date;
        const next = {
            start: range?.start || null,
            end: range?.end || null,
            [endpoint]: date,
        };
        const newRange =
            next.start && next.end ? ordered(next.start, next.end) : next;
        onRangeChange?.(newRange);
        setEndpoint(endpoint === "start" ? "end" : "start");

        if (
            (display === "dropdown" || display === "modal") &&
            newRange.start &&
            newRange.end
        ) {
            closeDropdown();
        }
    };
    const pointerDown = (
        event: PointerEvent<HTMLButtonElement>,
        date: string
    ): void => {
        if (mode !== "range" || event.button !== 0 || event.shiftKey) {
            return;
        }
        drag.current = {
            date,
            moved: false,
            previous: { start: range?.start || null, end: range?.end || null },
        };
        suppressClick.current = false;
        event.currentTarget.setPointerCapture(event.pointerId);
    };
    const pointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const target = document
            .elementFromPoint(event.clientX, event.clientY)
            ?.closest<HTMLButtonElement>("button[data-date]");
        const date = target?.dataset.date;
        if (
            !date ||
            !event.currentTarget.contains(target) ||
            disabledFn(date)
        ) {
            return;
        }
        setHover(date);
        if (
            !drag.current ||
            (date === drag.current.date && !drag.current.moved)
        ) {
            return;
        }
        drag.current.moved = true;
        suppressClick.current = true;
        anchor.current = drag.current.date;
        onRangeChange?.(ordered(drag.current.date, date));
    };
    const changeTime = (next: string): void =>
        onChange?.(
            next
                ? type === "time"
                    ? next
                    : `${selected || today}T${next}`
                : null
        );
    const navigate = (direction: number): void =>
        setMonth(
            new Date(
                month.getFullYear() +
                    (view === "years"
                        ? direction * 12
                        : view === "months"
                          ? direction
                          : 0),
                month.getMonth() + (view === "days" ? direction : 0),
                1
            )
        );
    const base = new Date(month.getFullYear(), month.getMonth(), 1);
    base.setDate(1 - ((base.getDay() + 6) % 7));
    const cells = Array.from(
        { length: 42 },
        (_, index) =>
            new Date(
                base.getFullYear(),
                base.getMonth(),
                base.getDate() + index
            )
    );
    const navBtn =
        "rounded-xl px-3 py-2 text-xs font-bold border border-primary text-primary bg-transparent hover:bg-slate-50 dark:border-primary dark:text-primary-100 dark:hover:bg-zinc-800 cursor-pointer transition-colors";
    const shortcutBtn =
        "rounded-xl px-3 py-2 text-xs font-bold border border-primary text-primary bg-transparent hover:bg-slate-50 dark:border-primary dark:text-primary-100 dark:hover:bg-zinc-800 cursor-pointer transition-colors";
    const gridBtn =
        "rounded-xl px-3 py-2 text-xs font-bold bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 cursor-pointer";
    const preview =
        range?.start && !range.end && hover
            ? ordered(range.start, hover)
            : range;

    const drillDown = (): void => {
        if (view === "days") {
            setView("months");
        } else if (view === "months") {
            setView("years");
        }
    };

    const drillUp = (): void => {
        if (view === "years") {
            setView("months");
        } else if (view === "months") {
            setView("days");
        }
    };

    const headerTitle =
        view === "days"
            ? `${month.toLocaleDateString(i18n.language, { month: "long" })} ${month.getFullYear()}`
            : view === "months"
              ? String(month.getFullYear())
              : `${month.getFullYear() - 5} – ${month.getFullYear() + 6}`;

    const calendarContent = (
        <div className={`space-y-3 ${display === "inline" ? className : ""}`}>
            {mode === "range" && (
                <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-x-5 gap-y-2 px-1">
                    {(["start", "end"] as const).map((edge) => (
                        <div
                            key={edge}
                            className={`flex min-w-0 items-center gap-2 border-b py-2 transition-colors ${endpoint === edge ? "border-primary text-primary" : "border-slate-200 text-slate-500 dark:border-zinc-700 dark:text-zinc-400"}`}
                        >
                            <button
                                type="button"
                                onClick={() => setEndpoint(edge)}
                                className="shrink-0 text-xs font-semibold cursor-pointer"
                            >
                                {t(
                                    `datepicker.${edge === "start" ? "from" : "to"}`
                                )}
                            </button>
                            <div className="flex min-w-0 flex-1 items-center gap-1">
                                <input
                                    aria-label={t(
                                        `datepicker.${edge === "start" ? "from" : "to"}`
                                    )}
                                    type="date"
                                    value={range?.[edge] || ""}
                                    min={
                                        edge === "end"
                                            ? range?.start || minDate
                                            : minDate
                                    }
                                    max={
                                        edge === "start"
                                            ? range?.end || maxDate
                                            : maxDate
                                    }
                                    onFocus={() => setEndpoint(edge)}
                                    onChange={(event) => {
                                        const date = event.target.value || null;
                                        if (date && disabledFn(date)) {
                                            return;
                                        }
                                        const next = {
                                            start: range?.start || null,
                                            end: range?.end || null,
                                            [edge]: date,
                                        };
                                        onRangeChange?.(
                                            next.start && next.end
                                                ? ordered(next.start, next.end)
                                                : next
                                        );
                                        if (date) {
                                            setMonth(
                                                new Date(`${date}T12:00:00`)
                                            );
                                        }
                                    }}
                                    className="min-w-0 w-full bg-transparent py-0.5 text-xs text-slate-800 outline-none dark:text-zinc-100 dark:[color-scheme:dark]"
                                />
                                <button
                                    type="button"
                                    aria-label={`${t("datepicker.clear")} ${t(`datepicker.${edge === "start" ? "from" : "to"}`)}`}
                                    onClick={() =>
                                        onRangeChange?.({
                                            start: range?.start || null,
                                            end: range?.end || null,
                                            [edge]: null,
                                        })
                                    }
                                    className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 cursor-pointer"
                                >
                                    <Icon value="x" size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {type !== "time" && (
                <div
                    className={`rounded-2xl bg-white dark:bg-zinc-900 select-none ${display === "inline" ? "border border-slate-200 dark:border-zinc-800 p-3.5 shadow-2xs" : display === "modal" ? "p-2" : "p-2 shadow-2xs"}`}
                >
                    <div className="flex items-center justify-between mb-3 gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                view !== "days" ? drillUp() : navigate(-1)
                            }
                            aria-label={
                                view !== "days"
                                    ? t("datepicker.back")
                                    : t("datepicker.prev_month")
                            }
                            className={navBtn}
                        >
                            <Icon value="chevronLeft" size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={view !== "years" ? drillDown : undefined}
                            disabled={view === "years"}
                            className={`font-black text-sm capitalize cursor-pointer text-center min-w-0 truncate flex-1 ${view === "years" ? "cursor-default" : "hover:text-primary transition-colors"}`}
                        >
                            {headerTitle}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(1)}
                            aria-label={t("datepicker.next_month")}
                            className={`${navBtn} shrink-0`}
                        >
                            <Icon value="chevronRight" size={16} />
                        </button>
                    </div>
                    {view !== "days" ? (
                        <div className="grid grid-cols-3 gap-2">
                            {Array.from({ length: 12 }, (_, index) => (
                                <button
                                    type="button"
                                    key={index}
                                    className={`${gridBtn} h-14`}
                                    onClick={() => {
                                        setMonth(
                                            new Date(
                                                view === "years"
                                                    ? month.getFullYear() -
                                                      5 +
                                                      index
                                                    : month.getFullYear(),
                                                view === "months"
                                                    ? index
                                                    : month.getMonth(),
                                                1
                                            )
                                        );
                                        setView(
                                            view === "years" ? "months" : "days"
                                        );
                                    }}
                                >
                                    {view === "years"
                                        ? month.getFullYear() - 5 + index
                                        : new Date(
                                              2026,
                                              index,
                                              1
                                          ).toLocaleDateString(i18n.language, {
                                              month: "short",
                                          })}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-7 text-center text-[11px] font-black text-slate-400 mb-1 uppercase">
                                {Array.from({ length: 7 }, (_, i) => (
                                    <span key={i}>
                                        {new Date(2026, 0, 5 + i)
                                            .toLocaleDateString(i18n.language, {
                                                weekday: "short",
                                            })
                                            .slice(0, 2)}
                                    </span>
                                ))}
                            </div>
                            <div
                                className={`grid grid-cols-7 gap-1 ${mode === "range" ? "touch-none" : ""}`}
                                onPointerMove={pointerMove}
                                onPointerUp={() => {
                                    drag.current = null;
                                }}
                                onPointerCancel={() => {
                                    if (drag.current?.moved) {
                                        onRangeChange?.(drag.current.previous);
                                    }
                                    drag.current = null;
                                    suppressClick.current = false;
                                }}
                                onPointerLeave={() => setHover(null)}
                            >
                                {cells.map((date) => {
                                    const key = ymd(date);
                                    const edge =
                                        mode === "range"
                                            ? key === range?.start ||
                                              key === range?.end
                                            : key === selected;
                                    const inside =
                                        mode === "range" &&
                                        preview?.start &&
                                        preview.end &&
                                        key >= preview.start &&
                                        key <= preview.end;
                                    return (
                                        <button
                                            type="button"
                                            key={key}
                                            data-date={key}
                                            disabled={disabledFn(key)}
                                            aria-label={date.toLocaleDateString(
                                                i18n.language,
                                                { dateStyle: "full" }
                                            )}
                                            aria-pressed={edge}
                                            onPointerDown={(event) =>
                                                pointerDown(event, key)
                                            }
                                            onClick={(event) => {
                                                if (suppressClick.current) {
                                                    suppressClick.current =
                                                        false;
                                                    return;
                                                }
                                                choose(key, event.shiftKey);
                                            }}
                                            className={`h-10 text-xs font-bold transition-colors disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer ${edge ? "rounded-xl bg-primary text-white shadow-sm" : inside ? "bg-primary/15 text-primary" : `rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-800 ${date.getMonth() !== month.getMonth() ? "text-slate-300 dark:text-zinc-600" : "text-slate-800 dark:text-zinc-200"}`} ${key === today && !edge ? "ring-1 ring-inset ring-primary" : ""}`}
                                        >
                                            {date.getDate()}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-center gap-2 mt-3">
                                {[0, 1, 7].map((offset) => (
                                    <button
                                        type="button"
                                        key={offset}
                                        className={shortcutBtn}
                                        onClick={() => {
                                            const date = new Date();
                                            date.setDate(
                                                date.getDate() + offset
                                            );
                                            choose(ymd(date));
                                            setMonth(date);
                                            setView("days");
                                        }}
                                    >
                                        {t(
                                            `datepicker.${offset === 0 ? "today" : offset === 1 ? "tomorrow" : "next_week"}`
                                        )}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            {type !== "date" && (
                <div
                    className={`rounded-2xl bg-white dark:bg-zinc-900 text-center space-y-3 ${display === "inline" ? "border border-slate-200 dark:border-zinc-800 p-3.5" : "p-2"}`}
                >
                    <label className="flex flex-col items-center gap-2 text-xs font-bold">
                        <Icon value="clock" size={16} />
                        {t("datepicker.time")}
                        <input
                            type="time"
                            value={time}
                            onChange={(event) => changeTime(event.target.value)}
                            className="rounded-xl border border-slate-200 dark:border-zinc-700 p-3 text-xl font-mono bg-transparent dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100"
                        />
                    </label>
                    <div className="flex justify-center gap-2">
                        {["00", "15", "30", "45"].map((minute) => (
                            <button
                                type="button"
                                key={minute}
                                className={shortcutBtn}
                                onClick={() =>
                                    changeTime(`${time.slice(0, 2)}:${minute}`)
                                }
                            >
                                :{minute}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    if (display === "inline") {
        return calendarContent;
    }

    const hasSelection =
        mode === "range" ? Boolean(range?.start || range?.end) : Boolean(value);

    let displayValue = placeholder || (type === "time" ? "--:--" : "");
    const dateFormat: Intl.DateTimeFormatOptions = format || {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    };
    if (type === "time") {
        displayValue = value || placeholder || "--:--";
    } else if (mode === "single" && value) {
        displayValue = new Date(`${value}T12:00:00`).toLocaleDateString(
            i18n.language,
            dateFormat
        );
    } else if (mode === "range" && (range?.start || range?.end)) {
        const s = range?.start
            ? new Date(`${range.start}T12:00:00`).toLocaleDateString(
                  i18n.language,
                  dateFormat
              )
            : "?";
        const e = range?.end
            ? new Date(`${range.end}T12:00:00`).toLocaleDateString(
                  i18n.language,
                  dateFormat
              )
            : "?";
        displayValue = `${s} - ${e}`;
    }

    const animationClass = isClosing
        ? dropdownCoords.placement === "top"
            ? "select-dropdown--closing-top"
            : "select-dropdown--closing-bottom"
        : dropdownCoords.placement === "top"
          ? "select-dropdown--open-top"
          : "select-dropdown--open-bottom";

    return (
        <div
            className={`relative ${full ? "block w-full" : "inline-block"} ${className}`}
            ref={containerRef}
        >
            {label && (
                <label
                    htmlFor={id}
                    className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1"
                >
                    {label}{" "}
                    {required && <span className="text-primary">*</span>}
                </label>
            )}
            <div className="relative">
                <button
                    id={id}
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    onClick={() =>
                        !isOpen ? setIsOpen(true) : closeDropdown()
                    }
                    aria-expanded={isOpen}
                    aria-label={label || placeholder || t("datepicker.title")}
                    aria-invalid={Boolean(error) || undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    aria-haspopup="dialog"
                    data-form-value={JSON.stringify(
                        mode === "range" ? range : value
                    )}
                    className={`select-trigger w-full bg-white hover:bg-slate-50 dark:hover:bg-dark-subtle border ${error ? "border-rose-500 dark:border-rose-500" : "border-slate-200"} rounded-xl font-medium text-slate-800 dark:text-zinc-100 flex items-center justify-between gap-1.5 transition-all shadow-xs focus:outline-none focus:ring-0 focus:border-slate-200 outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed py-2.5 px-3.5 text-xs`}
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Icon
                            value={type === "time" ? "clock" : "calendar"}
                            size={15}
                            className="text-slate-400 dark:text-zinc-400 shrink-0"
                        />
                        <span
                            className={`truncate text-left whitespace-nowrap ${hasSelection ? "text-slate-900 dark:text-white font-semibold" : "text-slate-400 dark:text-zinc-400"}`}
                        >
                            {displayValue}
                        </span>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        {display === "dropdown" && (
                            <Icon
                                value="chevronDown"
                                size={14}
                                className={`text-slate-400 dark:text-zinc-400 shrink-0 transition-transform duration-200 ${isOpen && !isClosing ? "rotate-180 text-primary dark:text-white" : ""}`}
                            />
                        )}
                    </div>
                </button>
                {clearable && hasSelection && !disabled && (
                    <button
                        type="button"
                        aria-label={t("datepicker.clear")}
                        onClick={() => {
                            if (mode === "single") {
                                onChange?.(null);
                            } else {
                                onRangeChange?.({ start: null, end: null });
                            }
                        }}
                        className="absolute right-7 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-500"
                    >
                        <Icon value="x" size={16} />
                    </button>
                )}
            </div>

            {error && typeof error === "string" && (
                <p
                    id={`${id}-error`}
                    className="mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium"
                >
                    {error}
                </p>
            )}

            {display === "dropdown" &&
                (isOpen || isClosing) &&
                typeof document !== "undefined" &&
                createPortal(
                    <>
                        <div
                            className="fixed inset-0 z-[120000] bg-transparent cursor-default"
                            data-overlay-owner={owner || undefined}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                closeDropdown();
                            }}
                        />
                        <div
                            ref={dropdownRef}
                            data-overlay-owner={owner || undefined}
                            onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    closeDropdown();
                                    triggerRef.current?.focus();
                                }
                            }}
                            style={{
                                position: "fixed",
                                zIndex: 120001,
                                ...(dropdownCoords.placement === "top"
                                    ? { bottom: dropdownCoords.bottom }
                                    : { top: dropdownCoords.top }),
                                left: dropdownCoords.left,
                                width: dropdownCoords.width
                                    ? `${dropdownCoords.width}px`
                                    : "100%",
                                minWidth: "280px",
                                maxWidth: "100%",
                            }}
                            className={`select-dropdown ${animationClass} bg-white dark:bg-dark-box rounded-xl border border-slate-200/90 dark:border-white/10 shadow-2xl overflow-hidden p-2 ${dropdownClassName}`}
                        >
                            {calendarContent}
                        </div>
                    </>,
                    document.body
                )}

            {display === "modal" && (
                <Modal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    maxWidth="sm"
                    title={
                        placeholder ||
                        (type === "time"
                            ? t("datepicker.select_time")
                            : t("datepicker.select_date"))
                    }
                    footer={
                        <>
                            {hasSelection && (
                                <button
                                    type="button"
                                    className="btn danger"
                                    onClick={() => {
                                        if (mode === "single") onChange?.(null);
                                        else
                                            onRangeChange?.({
                                                start: null,
                                                end: null,
                                            });
                                        setIsOpen(false);
                                    }}
                                >
                                    {t("datepicker.clear")}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn secondary"
                                onClick={() => setIsOpen(false)}
                            >
                                {t("modal.close")}
                            </button>
                        </>
                    }
                >
                    <div className="py-2">{calendarContent}</div>
                </Modal>
            )}
            {error && error.trim() && (
                <p className="mt-1 text-xs text-rose-500 font-medium">
                    {error}
                </p>
            )}
        </div>
    );
};
